/**
 * Card Component
 * Reusable card container with optional header and footer
 */

export interface CardProps {
    children: React.ReactNode;
    className?: string;
    padding?: 'none' | 'sm' | 'md' | 'lg';
    shadow?: 'none' | 'sm' | 'md' | 'lg';
    border?: boolean;
    rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

export function Card({
    children,
    className = '',
    padding = 'md',
    shadow = 'sm',
    border = false,
    rounded = 'lg',
}: CardProps) {
    const paddingClasses = {
        none: '',
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-6',
    };

    const shadowClasses = {
        none: '',
        sm: 'shadow-sm',
        md: 'shadow-md',
        lg: 'shadow-lg',
    };

    const roundedClasses = {
        none: '',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
    };

    return (
        <div
            className={`
                bg-white
                ${paddingClasses[padding]}
                ${shadowClasses[shadow]}
                ${roundedClasses[rounded]}
                ${border ? 'border border-gray-200' : ''}
                ${className}
            `}
        >
            {children}
        </div>
    );
}

export interface CardHeaderProps {
    children: React.ReactNode;
    className?: string;
    action?: React.ReactNode;
}

export function CardHeader({ children, className = '', action }: CardHeaderProps) {
    return (
        <div className={`flex items-center justify-between pb-4 border-b border-gray-200 mb-4 ${className}`}>
            <h3 className="text-lg font-semibold text-gray-900">{children}</h3>
            {action}
        </div>
    );
}

export interface CardFooterProps {
    children: React.ReactNode;
    className?: string;
}

export function CardFooter({ children, className = '' }: CardFooterProps) {
    return (
        <div className={`pt-4 border-t border-gray-200 mt-4 ${className}`}>
            {children}
        </div>
    );
}

export default Card;
